import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * The house button (docs/design-guidelines.md §7). The variants ARE the
 * recipes, so a call site should pick a variant and add layout classes only:
 *
 *   <Button>Save</Button>                       primary — one per screen region
 *   <Button variant="outline">Cancel</Button>   secondary
 *   <Button variant="ghost">Edit</Button>       row / footer action
 *
 * Sizes are the same idea: `xs` (h-7, text-xs) is the row action inside a
 * card or table, `sm` (h-9) the toolbar / filter-row button, `default` (h-10)
 * the form button. Pick a size instead of restating `h-7 px-2 text-xs`.
 *
 * Don't restate a variant's colours at the call site (`bg-action-600
 * hover:bg-action-700 text-white` on default, `border-gray-200 text-gray-700
 * hover:bg-gray-50` on outline, `text-gray-600 hover:bg-gray-100` on ghost),
 * and never add a shadow, lift or gradient — hover changes colour, not
 * elevation. `tests/unit/buttonVariants.test.jsx` fails the build on both, and
 * on a raw <button>, <Link> or <a> that hand-rolls any of the three recipes.
 *
 * There is deliberately no `secondary` variant: the shadcn one resolved to the
 * same solid blue as `default` through the `--secondary` token, so "secondary"
 * meant nothing. The secondary look is `outline`.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-action-600 text-white hover:bg-action-700",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-gray-200 bg-background text-gray-700 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900",
        ghost: "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-7 gap-1.5 rounded-md px-2.5 text-xs",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        // The round icon button in a utility bar (simulator, test taking,
        // persona chat): pair with variant="outline" and one 16px icon.
        iconRound: "h-8 w-8 rounded-full p-0 sm:h-9 sm:w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
